// ─────────────────────────────────────────────────────────────────────────────
// Global Search — tier-scoped feature/navigation search with executive stealth.
// Spec: executive-authority-rbac. Task 11.1.
//
// The pure core of Global Search / the command palette / feature discovery. It
// searches the navigation surface (the app's discoverable features) and applies
// the SAME stealth role gate the sidebar uses (`navRoleVisible`), so executive
// surfaces (e.g. the CEO Command Center, gated to `superadmin`) and any hidden
// executive feature NEVER appear in results for a non-executive tier — no label,
// no path, no discovery (Req 8.1, 8.2, 9.1; EA-11). The server remains the
// authoritative gate: targeting an executive route still returns an identical
// 403 regardless of what the client renders.
//
// Pure + deterministic (no network, no DOM) → unit/property-testable offline.
// Entity result search (leads/deals/…) is performed server-side through the
// already scope-guarded list endpoints; this module governs FEATURE discovery.
// ─────────────────────────────────────────────────────────────────────────────

import { NAV_GROUPS, navRoleVisible, type NavItem } from '../../routes/paths.ts';
import type { WorkspaceAccessKey } from '../../shared/permissions/workspaceAccess.ts';
import type { LucideIcon } from 'lucide-react';

export interface SearchResult {
  label: string;
  path: string;
  group?: string;
  icon: LucideIcon;
}

/** Flatten all nav items to {item, group}, preserving their section label. */
function flatNav(): { item: NavItem; group?: string }[] {
  return NAV_GROUPS.flatMap((g) => g.items.map((item) => ({ item, group: g.label })));
}

/**
 * Tier-scoped feature search. Returns only features the caller's role may see
 * (stealth: executive-only surfaces are excluded for every non-executive tier),
 * matched case-insensitively against the label. An empty/whitespace query
 * returns no results (the palette shows recents/defaults instead).
 *
 * @param role  the caller's raw backend role (server stays authoritative)
 * @param query the search text
 */
export function searchFeatures(
  role: string | null | undefined,
  query: string,
  department?: string | null,
  workspaceAccess?: readonly WorkspaceAccessKey[],
): SearchResult[] {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return [];
  return flatNav()
    .filter(({ item }) => navRoleVisible(item, role, department, workspaceAccess)) // stealth gate
    .filter(({ item }) => item.label.toLowerCase().includes(q))
    .map(({ item, group }) => ({ label: item.label, path: item.path, group, icon: item.icon }));
}
